import { useEffect } from 'react'
import { Button, Drawer, Input, Select, useDrawer } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { useTranslation } from 'react-i18next'

import { SUPPORTED_COUNTRIES } from '@/common/constants/countries'
import { LANGUAGES } from '@/common/constants/languages'
import { capitalizeFirstLetter } from '@/common/utils/capitalizeFirstLetter'
import { useSelectedAccountStore } from '../stores/useSelectedAccountStore'

export function DrawerViewAccount() {
  const { t, i18n } = useTranslation(['sso', 'common'])
  const { close } = useDrawer('drawer-view-account')
  const hookForm = useForm()
  const { account, selectAccount } = useSelectedAccountStore()

  const handleClose = () => {
    hookForm.reset()
    selectAccount(null)
    close()
  }

  const regionName = new Intl.DisplayNames([i18n.language], {
    type: 'region',
  })

  const languageName = new Intl.DisplayNames([i18n.language], {
    type: 'language',
  })

  const COUNTRY_OPTIONS = SUPPORTED_COUNTRIES.map((country) => ({
    label: regionName.of(country) || '',
    value: country,
  }))

  const LANGUAGE_OPTIONS = LANGUAGES.map((language) => ({
    label: capitalizeFirstLetter(languageName.of(language) || ''),
    value: language,
  }))

  useEffect(() => {
    if (account) {
      hookForm.reset({
        title: account.title,
        accountKey: account.accountKey,
        country: account.country,
        description: account.description || '',
        locale: account.locale,
      })
    }
  }, [account, hookForm])

  return (
    <Drawer.Root id="drawer-view-account">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('sso:accounts.drawer_view_account.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('sso:accounts.drawer_view_account.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <form className="space-y-6">
            <div className="flex flex-1 gap-4 w-full">
              <div className="w-full">
                <Input.Base
                  disabled
                  name="accountKey"
                  label={t(
                    'sso:accounts.drawer_view_account.fields.accountKey.label',
                  )}
                />
              </div>

              <div className="w-full">
                <Input.Base
                  disabled
                  name="title"
                  label={t(
                    'sso:accounts.drawer_view_account.fields.title.label',
                  )}
                />
              </div>
            </div>

            <Input.Base
              disabled
              name="description"
              label={t(
                'sso:accounts.drawer_view_account.fields.description.label',
              )}
              placeholder={t(
                'sso:accounts.drawer_view_account.fields.description.placeholder',
              )}
            />

            <div className="flex flex-1 gap-4 w-full">
              <Select.Single
                disabled
                name="country"
                label={t(
                  'sso:accounts.drawer_view_account.fields.country.label',
                )}
                options={COUNTRY_OPTIONS}
              />

              <Select.Single
                disabled
                name="locale"
                label={t(
                  'sso:accounts.drawer_view_account.fields.locale.label',
                )}
                options={LANGUAGE_OPTIONS}
              />
            </div>
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-end gap-4">
          <Button.Root variant="secondary" color="brand" onClick={handleClose}>
            {t('sso:accounts.drawer_view_account.footer.button_close.label')}
          </Button.Root>
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
